'use client';

/**
 * FileDropzone Component
 *
 * Drag-and-drop file upload zone for the ImportWizard.
 * Supports CSV, Excel (.xlsx), and JSON file types.
 */

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import Box from '@mui/material/Box';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { useCallback, useRef, useState } from 'react';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import { iconSizes } from '../../theme/typography';
import { formatBytes } from '../../utils/formatters';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export interface FileDropzoneProps {
  /**
   * Callback when a file is selected
   */
  onFileSelect: (file: File) => void;

  /**
   * Currently selected file
   */
  selectedFile?: File | null;

  /**
   * Accepted file types (MIME types or extensions)
   * @default [".csv", ".xlsx", ".xls", ".json"]
   */
  acceptedFileTypes?: string[];

  /**
   * Maximum file size in bytes
   * @default 10MB
   */
  maxFileSize?: number;

  /**
   * Error message to display
   */
  error?: string;

  /**
   * Whether the dropzone is disabled
   */
  disabled?: boolean;

  /**
   * Neon accent color
   * @default "cyan"
   */
  neonAccent?: NeonAccent;

  /**
   * Additional class name
   */
  className?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledDropZoneProps {
  $isDark: boolean;
  $neonAccent: NeonAccent;
  $hasError: boolean;
  $isDragActive: boolean;
  $hasFile: boolean;
  $disabled: boolean;
}

const StyledDropZone = styled(motion.div, {
  shouldForwardProp: (prop) => !String(prop).startsWith('$'),
})<StyledDropZoneProps>(({ theme, $isDark, $neonAccent, $hasError, $isDragActive, $hasFile, $disabled }) => {
  const neonStyles = getNeonStyles($neonAccent, $isDark);
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    minHeight: 200,
    borderRadius: 16,
    border: `2px dashed ${
      $hasError
        ? theme.palette.error.main
        : $isDragActive || $hasFile
          ? neonStyles.base
          : $isDark
            ? 'rgba(255, 255, 255, 0.2)'
            : 'rgba(0, 0, 0, 0.15)'
    }`,
    backgroundColor: $isDragActive
      ? alpha(neonStyles.base, $isDark ? 0.12 : 0.08)
      : $hasFile
        ? alpha(neonStyles.base, $isDark ? 0.06 : 0.04)
        : glass.background,
    backdropFilter: glass.backdropBlur,
    transition: transitionPresets.card,
    cursor: $disabled ? 'not-allowed' : 'pointer',
    opacity: $disabled ? 0.6 : 1,
    position: 'relative',
    overflow: 'hidden',

    '&:hover': $disabled
      ? {}
      : {
          borderColor: $hasError ? theme.palette.error.main : neonStyles.base,
          backgroundColor: alpha(neonStyles.base, $isDark ? 0.1 : 0.06),
          boxShadow: `0 0 20px ${alpha(neonStyles.base, 0.2)}`,
        },

    // Subtle gradient overlay when dragging
    '&::before': $isDragActive
      ? {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(circle at center, ${alpha(neonStyles.base, 0.15)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }
      : {},
  };
});

const StyledFileInfo = styled(
  Box,
  transientOptions,
)<{
  $isDark: boolean;
  $neonAccent: NeonAccent;
}>(({ $isDark, $neonAccent }) => {
  const neonStyles = getNeonStyles($neonAccent, $isDark);
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '16px 24px',
    borderRadius: 12,
    backgroundColor: glass.background,
    border: `1px solid ${neonStyles.border}`,
    width: '100%',
    maxWidth: 400,
  };
});

const StyledFileIcon = styled(
  Box,
  transientOptions,
)<{
  $neonAccent: NeonAccent;
  $isDark: boolean;
}>(({ $neonAccent, $isDark }) => {
  const neonStyles = getNeonStyles($neonAccent, $isDark);

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: alpha(neonStyles.base, 0.1),
    color: neonStyles.base,
    flexShrink: 0,
  };
});

// ============================================================================
// Helpers
// ============================================================================

const DEFAULT_ACCEPTED_TYPES = ['.csv', '.xlsx', '.xls', '.json'];
const DEFAULT_MAX_SIZE = 10 * 1024 * 1024; // 10MB

const getFileExtension = (filename: string): string => {
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext ? `.${ext}` : '';
};

const getFileTypeLabel = (filename: string): string => {
  const ext = getFileExtension(filename).toLowerCase();
  switch (ext) {
    case '.csv':
      return 'CSV File';
    case '.xlsx':
    case '.xls':
      return 'Excel File';
    case '.json':
      return 'JSON File';
    default:
      return 'File';
  }
};

// ============================================================================
// Component
// ============================================================================

export const FileDropzone: React.FC<FileDropzoneProps> = ({
  onFileSelect,
  selectedFile,
  acceptedFileTypes = DEFAULT_ACCEPTED_TYPES,
  maxFileSize = DEFAULT_MAX_SIZE,
  error,
  disabled = false,
  neonAccent = 'cyan',
  className,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const displayError = error || localError;

  // Validate file
  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file type
      const ext = getFileExtension(file.name);
      const isValidType = acceptedFileTypes.some(
        (type) =>
          type === ext || type === file.type || (type.endsWith('/*') && file.type.startsWith(type.slice(0, -1))),
      );

      if (!isValidType) {
        return `Invalid file type. Accepted types: ${acceptedFileTypes.join(', ')}`;
      }

      // Check file size
      if (file.size > maxFileSize) {
        return `File size exceeds maximum of ${formatBytes(maxFileSize)}`;
      }

      return null;
    },
    [acceptedFileTypes, maxFileSize],
  );

  // Handle file selection
  const handleFileSelect = useCallback(
    (file: File) => {
      const validationError = validateFile(file);
      if (validationError) {
        setLocalError(validationError);
        return;
      }

      setLocalError(null);
      onFileSelect(file);
    },
    [validateFile, onFileSelect],
  );

  // Handle click
  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Drag handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (disabled) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const neonStyles = getNeonStyles(neonAccent, isDark);

  return (
    <Box className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFileTypes.join(',')}
        onChange={handleInputChange}
        style={{ display: 'none' }}
        disabled={disabled}
      />
      <StyledDropZone
        $isDark={isDark}
        $neonAccent={neonAccent}
        $hasError={!!displayError}
        $isDragActive={isDragActive}
        $hasFile={!!selectedFile}
        $disabled={disabled}
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        whileHover={disabled ? {} : { scale: 1.005 }}
        whileTap={disabled ? {} : { scale: 0.995 }}
      >
        <AnimatePresence mode="wait">
          {selectedFile ? (
            <motion.div
              key="file-selected"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              <StyledFileInfo $isDark={isDark} $neonAccent={neonAccent}>
                <StyledFileIcon $neonAccent={neonAccent} $isDark={isDark}>
                  <DescriptionIcon />
                </StyledFileIcon>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {selectedFile.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {getFileTypeLabel(selectedFile.name)} - {formatBytes(selectedFile.size)}
                  </Typography>
                </Box>
              </StyledFileInfo>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mt: 2,
                  textAlign: 'center',
                }}
              >
                Click or drop a new file to replace
              </Typography>
            </motion.div>
          ) : (
            <motion.div
              key="no-file"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <motion.div
                animate={{
                  y: isDragActive ? -8 : 0,
                  scale: isDragActive ? 1.1 : 1,
                }}
                transition={{ duration: 0.2 }}
              >
                <CloudUploadIcon
                  sx={{
                    fontSize: iconSizes['4xl'],
                    color: isDragActive ? neonStyles.base : isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.25)',
                    mb: 2,
                    transition: transitionPresets.colors,
                  }}
                />
              </motion.div>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: isDragActive ? neonStyles.base : 'text.primary',
                  mb: 1,
                  transition: transitionPresets.colors,
                }}
              >
                {isDragActive ? 'Drop your file here' : 'Drag & drop your file'}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  mb: 2,
                }}
              >
                or click to browse
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                }}
              >
                {acceptedFileTypes.map((type) => (
                  <Box
                    key={type}
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      backgroundColor: alpha(neonStyles.base, isDark ? 0.1 : 0.08),
                      color: neonStyles.base,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                    }}
                  >
                    {type.replace('.', '')}
                  </Box>
                ))}
              </Box>

              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  mt: 2,
                }}
              >
                Max file size: {formatBytes(maxFileSize)}
              </Typography>
            </motion.div>
          )}
        </AnimatePresence>
      </StyledDropZone>
      {displayError && (
        <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}>
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {displayError}
          </Typography>
        </motion.div>
      )}
    </Box>
  );
};

FileDropzone.displayName = 'FileDropzone';

export default FileDropzone;
